import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useState } from "react";
import { Loader2, Check, X, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { PageHeader, Card } from "../FinanceApp";
import { Button } from "../../components/ui/button";

const ENTITY_LABEL = { journal: "قيد يومية", document: "فاتورة", payment: "سند" };
const MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

export function FinApprovals() {
  const [d, setD] = useState(null);
  const load = async () => { try { setD(await finApi.pendingApprovals()); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); }, []);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="fin-approvals">
      <PageHeader title="الموافقات" subtitle="اعتماد الفواتير وسندات الصرف والقيود بانتظار الاعتماد" />
      {!d ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
        <Card className="overflow-hidden">
          {d.pending.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="fin-no-pending">لا توجد مستندات بانتظار الاعتماد</div> : (
            <div className="overflow-x-auto"><table className="w-full text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">النوع</th><th className="px-3 py-2 font-semibold">الرقم</th><th className="px-3 py-2 font-semibold">التاريخ</th><th className="px-3 py-2 font-semibold">الطرف/البيان</th><th className="px-3 py-2 font-semibold">المبلغ</th><th className="px-3 py-2 font-semibold">إجراء</th></tr></thead>
              <tbody className="font-mono-nums">{d.pending.map((p) => (
                <tr key={p.id} data-testid="fin-pending-row" className="border-t border-slate-50">
                  <td className="px-3 py-2 font-sans">{ENTITY_LABEL[p.entity]}</td><td className="px-3 py-2">{p.number}</td><td className="px-3 py-2 text-slate-500">{p.date}</td>
                  <td className="px-3 py-2 font-sans">{p.party_name || p.description || "—"}</td><td className="px-3 py-2" dir="ltr">{money(p.amount)} <SaudiRiyal /></td>
                  <td className="px-3 py-2">{d.is_approver ? <div className="flex gap-1">
                    <button onClick={() => act(() => finApi.approve(p.entity, p.id), "تم الاعتماد")} data-testid="fin-approve-btn" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
                    <button onClick={() => act(() => finApi.rejectApproval(p.entity, p.id, "مرفوض"), "تم الرفض")} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                  </div> : <span className="text-xs text-slate-400">بانتظار المعتمد</span>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      )}
    </div>
  );
}

export function FinPeriods() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const load = async (y) => { try { setD(await finApi.periods(y)); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(year); }, [year]);
  const toggle = async (p) => {
    try { if (p.status === "closed") await finApi.openPeriod(p.key); else await finApi.closePeriod(p.key); toast.success("تم التحديث"); load(year); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const closeYr = async () => { if (!window.confirm(`إقفال السنة المالية ${year} بالكامل؟`)) return; try { await finApi.closeYear(year); toast.success("تم إقفال السنة"); load(year); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="fin-periods">
      <PageHeader title="الفترات المالية" subtitle="فتح وإقفال الفترات والسنة المالية — الترحيل في فترة مقفلة ممنوع"
        action={<div className="flex gap-2 items-center">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="fin-period-year">{[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}</select>
          <Button onClick={closeYr} variant="outline" data-testid="fin-close-year" className="gap-1.5 border-rose-200 text-rose-700"><Lock className="h-4 w-4" /> إقفال السنة</Button>
        </div>} />
      {!d ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{d.periods.map((p) => (
          <Card key={p.key} className="p-4 flex items-center justify-between" data-testid={`fin-period-${p.key}`}>
            <div><div className="font-semibold text-slate-800">{MONTHS[p.month - 1]}</div><div className={`text-xs ${p.status === "closed" ? "text-rose-600" : "text-emerald-600"}`}>{p.status === "closed" ? "مقفلة" : "مفتوحة"}</div></div>
            <button onClick={() => toggle(p)} data-testid={`fin-toggle-${p.key}`} className={`p-2 rounded-lg ${p.status === "closed" ? "text-emerald-600 hover:bg-emerald-50" : "text-rose-600 hover:bg-rose-50"}`}>{p.status === "closed" ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</button>
          </Card>
        ))}</div>
      )}
    </div>
  );
}
